import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { FeedbackContainer, ScreenResponsive } from "../../components";
import {
	Box,
	BoxedRow,
	ButtonLayout,
	ButtonLink,
	ButtonPrimary,
	Grid,
	IconQuestionRegular,
	Text2,
	Text3,
	skinVars,
	useScreenSize,
	Callout,
	IconInformationUserRegular,
} from "@telefonica/mistica";
import { strings } from "../../localization";
import appFGTS from "../../assets/images/AppFGTS.png";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { useNavigateCustom, useOpenFGTSApp, useIsDateInRange } from "../../hooks";
import { useAnalytics } from "../../hooks/useAnalytics";
import { getFFSecondQiTechAccount } from "../../services/SystemConfiguration";
import { Image } from "@chakra-ui/react";
import { Store } from "./store";
import { DocumentosCopyBiometryAnalysisStatus, KYCBiometryAnalysisStatus } from "../../enum";

const { listSecondAccount, infoSecondAccount, cefSlow, screen, textButtonLink, textButtonPrimary, description, descriptionSecondAccount, slot, title } =
	strings.pages.authorizationInquiryBalance;

const AuthorizationInquiryBalance: React.FC = observer(() => {
	const { isDesktopOrBigger } = useScreenSize();
	const navigation = useNavigateCustom();
	const { authenticationStore, anticipationStore, paymentInfo, fintechRegisterStore } = useGlobalStore();
	const { addAnalyticEvent, updatePageView } = useAnalytics();
	const [isSecondAccount, setIsSecondAccount] = React.useState(false);

	// ✅ Memoiza a store para evitar recriações e chamadas duplicadas
	const storeAuth = useMemo(() => new Store(authenticationStore), [authenticationStore]);

	// Check if the current date is between the 20th and 25th day of the month
	const shouldShowCefSlowMessage = useIsDateInRange(20, 25);

	const handleAnalyticEvent = (buttonName: string, component?: "body" | "header"): void => {
		addAnalyticEvent({
			action: "click.button",
			component: component ? component : "body",
			description: buttonName,
			step: "fgts:autorizar-consulta-saldo",
			value: "",
		}, { applySanitize: true });
	};

	const handleGoBack = useCallback(() => {
		handleAnalyticEvent(screen, "header");
		navigation("/", { replace: true });
	}, []);

	const onHandleAuthorize = useCallback(() => {
		handleAnalyticEvent(textButtonPrimary);
		navigation("/connect-fgts-balance");
	}, []);

	const onHandleHowToAuthorize = useCallback(() => {
		handleAnalyticEvent(slot);
		navigation("/how-to-authorize");
	}, []);

	useEffect(() => {
		updatePageView({
			name: "Vivo Money FGTS Autorize Consulta do Saldo",
			pagePath: "/fgts/autorizar-consulta-saldo",
		});
	}, []);


	const handlePopState = (): void => {
		navigation("/", { replace: true });
		authenticationStore.logout();
	};

	useEffect(() => {

		// Adiciona um listener ao evento popstate
		window.addEventListener("popstate", handlePopState);

		// Limpa o listener ao desmontar o componente
		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [navigation]);

	useEffect(() => {
		anticipationStore.resetState();
		anticipationStore.partialAnticipationShelf.resetState();
		paymentInfo.resetState();
	}, []);

	// Busca valor da feature flag secondQiTechAccount
	const fetchFeatureFlag = useCallback(async (): Promise<void> => {
		const ffIsSecondAccount = await getFFSecondQiTechAccount();
		setIsSecondAccount(ffIsSecondAccount);
	}, []);

	useEffect(() => {
		fetchFeatureFlag();
	}, []);

	const fetchGetUser = useCallback(async (): Promise<void> => {
		await fintechRegisterStore.getPersonalFetch.fetch();

		if (fintechRegisterStore.personal.value) {
			await storeAuth.fraudAnalysisFetch.fetch();

			// Pega o status da DocumentosCopia
			const documentoscopyStatus = storeAuth.fraudAnalysis.value &&
				storeAuth.fraudAnalysis.value.documentoscopy.status;

			// Pega status do KYC
			const kycStatus = storeAuth.fraudAnalysis.value && storeAuth.fraudAnalysis.value.kyc.status;

			// Valida de esta reprovada
			if (documentoscopyStatus === DocumentosCopyBiometryAnalysisStatus.DISAPPROVED
				|| kycStatus === KYCBiometryAnalysisStatus.REPROVED || kycStatus === KYCBiometryAnalysisStatus.DISAPPROVED
			) {
				return navigation("/fintech-registration/validate-documents/refused");
			}
		}
	}, []);

	useEffect(() => {
		fetchGetUser();
	}, []);

	return (
		<ScreenResponsive navigationTitle={screen} onBack={handleGoBack}>
			<Box paddingY={isDesktopOrBigger ? 80 : 32} width="100%">
				<Grid
					alignContent="start"
					justifyContent="center"
					gap={0}
					alignItems="start"
					justifyItems="center"
					columns={isDesktopOrBigger ? 2 : 1}
					rows={1}
				>
					<Image
						mb={12}
						src={appFGTS}
						height={isDesktopOrBigger ? 620 : 370}
						width={isDesktopOrBigger ? 496 : 283}
						alt={screen}
					/>

					<FeedbackContainer
						isFullHeight={false}
						title={title}
						description={
							<Text3 light color={skinVars.colors.neutralMediumInverse}>
								{isSecondAccount ? descriptionSecondAccount[0] : description[0]}
								<Text3 medium color={skinVars.colors.neutralMediumInverse}>
									{isSecondAccount ? descriptionSecondAccount[1] : description[1]}
								</Text3>
								{isSecondAccount ? descriptionSecondAccount[2] : description[2]}
								{isSecondAccount && (
									<>
										<br />
										<br />
										<ul style={{ display: "flex", paddingLeft: 18, justifyContent: "center", alignItems: "flex-start", flexDirection: "column"}}>
											<li style={{ listStyleType: "circle" }}>
												<Text3 medium color={skinVars.colors.neutralMediumInverse}>
													{listSecondAccount[0]}
												</Text3>
											</li>

											<li style={{ listStyleType: "circle"}}>
												<Text3 medium color={skinVars.colors.neutralMediumInverse}>
													{listSecondAccount[1]}
												</Text3>
											</li>
										</ul>
										<br />
										{infoSecondAccount}
									</>
								)}
							</Text3>
						}
						slot={
							<Grid gap={32}>
								<BoxedRow
									asset={<IconQuestionRegular color={skinVars.colors.brand} />}
									title={slot}
									onPress={onHandleHowToAuthorize}
								/>
								{shouldShowCefSlowMessage && (
									<Callout icon={<IconInformationUserRegular color={skinVars.colors.brand} />} description={cefSlow} />
								)}
							</Grid>
						}
						width={392}
					>
						<Box paddingTop={32}>
							<ButtonLayout align={isDesktopOrBigger ? "left" : "center"}>
								<ButtonPrimary
									onPress={onHandleAuthorize}
									style={{ width: isDesktopOrBigger ? "auto" : "100%" }}
								>
									<Text3 medium color={skinVars.colors.textButtonPrimary}>
										{textButtonPrimary}
									</Text3>
								</ButtonPrimary>

								<ButtonLink
									onPress={() => {
										handleAnalyticEvent(textButtonLink);
										useOpenFGTSApp();
									}}
								>
									<Text2 medium color={skinVars.colors.textLink}>
										{textButtonLink}
									</Text2>
								</ButtonLink>
							</ButtonLayout>
						</Box>
					</FeedbackContainer>
				</Grid>
			</Box>
		</ScreenResponsive>
	);
});

export default AuthorizationInquiryBalance;
